/**
 * PricingPage — Página de precios del SaaS Shell.
 * Carga planes desde /api/onboarding/plans (no requiere auth).
 * Fallback a planes mock si el backend no responde.
 */
import { useEffect, useState } from "react";
import axios from "axios";

const API = process.env.REACT_APP_API_URL || "http://localhost:8000/api";

const FEATURE_LABELS = {
    pos: "TPV Completo",
    orders: "Gestión de Pedidos",
    qr_tables: "QR para Mesas",
    kitchen: "Pantalla de Cocina",
    delivery_own: "Delivery Propio",
    drivers: "Gestión de Riders Propios",
    integrations: "Glovo / Uber / JustEat",
    multi_printer: "Multi-impresora",
    multi_store: "Múltiples Locales",
    api: "API Pública",
    delivery_hub: "Delivery Hub (Live Map)",
};

const FALLBACK_PLANS = [
    {
        slug: "basic", name: "Plan Básico", price_monthly: 49, price_yearly: 490,
        description: "El sistema operativo esencial para TPV local.",
        features: { pos: true, orders: true, qr_tables: true, kitchen: true, delivery_own: false, drivers: false, integrations: false, multi_printer: false, multi_store: false, api: false, delivery_hub: false },
    },
    {
        slug: "pro", name: "Plan Profesional", price_monthly: 99, price_yearly: 990, popular: true,
        description: "Todo el poder del POS con delivery avanzado.",
        features: { pos: true, orders: true, qr_tables: true, kitchen: true, delivery_own: true, drivers: true, integrations: true, multi_printer: true, multi_store: false, api: false, delivery_hub: true },
    },
    {
        slug: "enterprise", name: "Plan Enterprise", price_monthly: 199, price_yearly: 1990,
        description: "Para cadenas y grupos de restauración.",
        features: { pos: true, orders: true, qr_tables: true, kitchen: true, delivery_own: true, drivers: true, integrations: true, multi_printer: true, multi_store: true, api: true, delivery_hub: true },
    },
    {
        slug: "delivery", name: "Delivery Only", price_monthly: 49, price_yearly: 490,
        description: "Enfocado exclusivamente en logística de reparto.",
        features: { pos: false, orders: true, qr_tables: false, kitchen: false, delivery_own: true, drivers: true, integrations: true, multi_printer: false, multi_store: false, api: false, delivery_hub: true },
    },
];

export default function PricingPage({ onCtaClick }) {
    const [plans, setPlans] = useState(FALLBACK_PLANS);
    const [yearly, setYearly] = useState(false);

    useEffect(() => {
        axios.get(`${API}/onboarding/plans`)
            .then((r) => { if (r.data?.length) setPlans(r.data); })
            .catch(() => { }); // fallback silencioso
    }, []);

    return (
        <section id="precios" style={s.section} className="page-fade-in">
            <div style={s.container}>
                <div style={s.header}>
                    <span style={s.pill}>💳 Planes</span>
                    <h2 style={s.title}>Sin sorpresas. Sin comisiones.</h2>
                    <p style={s.sub}>Paga solo por lo que necesitas. Cancela cuando quieras.</p>

                    {/* Toggle */}
                    <div style={s.toggle}>
                        <span style={{ color: !yearly ? "#fff" : "var(--text-soft)", fontWeight: !yearly ? "700" : "400" }}>Mensual</span>
                        <button id="pricing-cycle-toggle" onClick={() => setYearly(!yearly)} style={{
                            ...s.toggleBtn,
                            background: yearly ? 'var(--primary)' : 'rgba(255,255,255,0.1)'
                        }}>
                            <div style={{ ...s.toggleThumb, transform: yearly ? "translateX(20px)" : "translateX(0)" }} />
                        </button>
                        <span style={{ color: yearly ? "var(--accent)" : "var(--text-soft)", fontWeight: yearly ? "700" : "400" }}>
                            Pago Anual <span style={s.save}>−2 MESES GRATIS</span>
                        </span>
                    </div>
                </div>

                <div style={s.grid}>
                    {plans.map((plan, i) => {
                        const yearlyPrice = plan.price_yearly || (plan.price_monthly * 10);
                        const displayPrice = plan.price_monthly; // Always show the base monthly price
                        const pop = plan.popular || plan.slug === "pro";

                        return (
                            <div key={plan.slug || i} style={{ ...s.card, ...(pop ? s.cardPop : {}) }} className="glass-effect card-hover">
                                {pop && <div style={s.badge}>⭐ RECOMENDADO</div>}
                                <h3 style={s.planName}>{plan.name}</h3>
                                <p style={s.planDesc}>{plan.description}</p>
                                <div style={s.price}>
                                    <span style={s.priceNum}>{displayPrice}€</span>
                                    <span style={s.pricePer}>/mes</span>
                                </div>
                                {yearly && (
                                    <div style={{ color: "var(--accent)", fontSize: "0.85rem", marginTop: "-2rem", marginBottom: "2rem", fontWeight: "700" }}>
                                        Facturación anual de {yearlyPrice}€
                                    </div>
                                )}
                                {!yearly && (
                                    <div style={{ visibility: "hidden", fontSize: "0.85rem", marginTop: "-2rem", marginBottom: "2rem" }}>
                                        Spacer
                                    </div>
                                )}

                                <ul style={s.features}>
                                    {Object.entries(plan.features || {})
                                        .filter(([_, val]) => val === true) // Show only included features
                                        .map(([key, _]) =>
                                            FEATURE_LABELS[key] ? (
                                                <li key={key} style={s.feature}>
                                                    <span style={{ ...s.check, color: "var(--accent)" }}>✓</span>
                                                    <span style={{ color: "var(--text-main)", fontWeight: "500" }}>{FEATURE_LABELS[key]}</span>
                                                </li>
                                            ) : null
                                        )}
                                </ul>

                                <button
                                    id={`pricing-cta-${plan.slug}`}
                                    onClick={() => onCtaClick(plan.slug, yearly)}
                                    className="btn-premium"
                                    style={{
                                        width: "100%",
                                        background: pop ? "var(--primary)" : "rgba(255,255,255,0.05)",
                                        border: pop ? "none" : "1px solid var(--border-soft)",
                                        color: "#fff"
                                    }}
                                >
                                    Elegir {plan.name}
                                </button>
                            </div>
                        );
                    })}
                </div>
            </div>
        </section>
    );
}

const s = {
    section: { background: "var(--bg-main)", padding: "8rem 1rem" },
    container: { maxWidth: "1100px", margin: "0 auto" },
    header: { textAlign: "center", marginBottom: "5rem" },
    pill: {
        display: "inline-block", background: "rgba(59,130,246,0.1)",
        border: "1px solid rgba(59,130,246,0.2)", color: "var(--primary)",
        fontSize: "0.85rem", fontWeight: "700", borderRadius: "9999px",
        padding: "0.4rem 1.25rem", marginBottom: "1.5rem", textTransform: "uppercase", letterSpacing: "0.05em"
    },
    title: { color: "#fff", fontSize: "3rem", fontWeight: "900", margin: "0 0 1rem", letterSpacing: "-0.04em" },
    sub: { color: "var(--text-soft)", fontSize: "1.25rem", margin: "0 0 2.5rem" },
    toggle: { display: "flex", alignItems: "center", justifyContent: "center", gap: "1rem", fontSize: "1rem" },
    toggleBtn: {
        width: "48px", height: "28px",
        border: "none", borderRadius: "9999px", cursor: "pointer", position: "relative",
        transition: "all 0.3s ease",
    },
    toggleThumb: {
        position: "absolute", top: "4px", left: "4px",
        width: "20px", height: "20px", background: "#fff",
        borderRadius: "50%", transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
        boxShadow: "0 2px 4px rgba(0,0,0,0.2)"
    },
    save: { background: "rgba(52, 211, 153, 0.1)", color: "#34d399", fontSize: "0.75rem", fontWeight: "800", borderRadius: "6px", padding: "0.2rem 0.5rem", marginLeft: "0.5rem" },
    grid: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))", gap: "2rem" },
    card: {
        padding: "3rem 2.5rem", position: "relative",
        fontFamily: "var(--font-main, 'Inter', sans-serif)",
        display: "flex", flexDirection: "column"
    },
    cardPop: { border: "1px solid var(--primary)", boxShadow: "0 0 40px rgba(59, 130, 246, 0.1)" },
    badge: {
        position: "absolute", top: "1.25rem", right: "2rem",
        background: "var(--primary)", color: "#fff", fontSize: "0.7rem", fontWeight: "900",
        padding: "0.3rem 0.75rem", borderRadius: "9999px", whiteSpace: "nowrap",
        letterSpacing: "0.05em"
    },
    planName: { color: "#fff", fontSize: "1.5rem", fontWeight: "800", margin: "0 0 0.5rem", letterSpacing: "-0.02em" },
    planDesc: { color: "var(--text-soft)", fontSize: "0.95rem", margin: "0 0 2rem", lineHeight: 1.6 },
    price: { display: "flex", alignItems: "baseline", gap: "0.25rem", marginBottom: "2.5rem" },
    priceNum: { color: "#fff", fontSize: "3.5rem", fontWeight: "900", letterSpacing: "-0.05em" },
    pricePer: { color: "var(--text-soft)", fontSize: "1.1rem", fontWeight: "500" },
    features: { listStyle: "none", padding: 0, margin: "0 0 3rem", display: "flex", flexDirection: "column", gap: "1rem", flex: 1 },
    feature: { display: "flex", gap: "1rem", alignItems: "center", fontSize: "0.95rem" },
    check: { fontWeight: "900", flexShrink: 0, width: "1.25rem", textAlign: "center", fontSize: "1.1rem" },
};
